import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Card,
} from "@mui/material";
import { UserWithTopics, Topic, UserRole } from "../../context/ConfigContext/types";
import { confirmAlert } from "react-confirm-alert";

export enum UserFormType {
  MAIN = "Main",
  CLIENT = "Client",
}

export interface UserFormProps {
  item: UserWithTopics;
  topics?: Topic[];
  onUserUpdate?: (updatedUser: UserWithTopics) => void;
  onUserDelete?: (id: number) => void;
}

enum Action {
  CREATE = "Create",
  UPDATE = "Update",
  DELETE = "Delete",
}

const UserForm: React.FC<UserFormProps> = ({ item: user, topics, onUserUpdate, onUserDelete }) => {
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [email, setEmail] = useState(user.email);
  const [adObjectId, setAdObjectId] = useState(user.adObjectId);
  const [selectedTopics, setSelectedTopics] = useState(user.topics.map((topic) => topic.id));
  const [role, setRole] = useState(user.role);

  useEffect(() => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setAdObjectId(user.adObjectId);
    setSelectedTopics(user.topics.map((topic) => topic.id));
    setRole(user.role);
  }, [user]);

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleAdObjectIdChange = (e) => {
    setAdObjectId(e.target.value);
  };

  const handleTopicChange = (e) => {
    setSelectedTopics(e.target.value);
  };

  const handleRoleChange = (e) => {
    setRole(e.target.value);
  };

  const formValid = () => {
    return [firstName, lastName, email, adObjectId].some((value) => !value);
  };

  const handleSave = () => {
    const updatedUser = {
      ...user,
      firstName,
      lastName,
      email,
      role,
      adObjectId,
      topics: selectedTopics.map((topicId) => ({
        id: topicId,
        name: topics.find((topic) => topic.id === topicId)?.name || "",
      })),
    };
    onUserUpdate(updatedUser);
  };

  const handleUndo = () => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setRole(user.role);
    setSelectedTopics(user.topics.map((topic) => topic.id));
  };

  const isFormChanged = () => {
    return (
      firstName !== user.firstName ||
      lastName !== user.lastName ||
      email !== user.email ||
      adObjectId !== user.adObjectId ||
      role !== user.role ||
      JSON.stringify(selectedTopics.sort()) !== JSON.stringify(user.topics.map((topic) => topic.id).sort())
    );
  };

  const submit = (action: Action) => {
    if (user.id > 0 && action === Action.CREATE) {
      action = Action.UPDATE;
    }
    confirmAlert({
      title: action + " user id " + user.id,
      message: `Are you sure you want to ${action} user ${firstName} ${lastName}?`,
      buttons: [
        {
          label: action,
          onClick:
            action === Action.CREATE || action === Action.UPDATE ? () => handleSave() : () => onUserDelete(user.id),
        },
        {
          label: "Cancel",
        },
      ],
    });
  };

  return (
    <Box>
      <Card sx={{ padding: 2 }}>
        <h2>User with id {user.id}</h2>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <TextField label="First Name" value={firstName} onChange={handleFirstNameChange} />
          </Grid>
          <Grid item>
            <TextField label="Last Name" value={lastName} onChange={handleLastNameChange} />
          </Grid>
          <Grid item>
            <TextField label="Email" value={email} onChange={handleEmailChange} />
          </Grid>
          <Grid item>
            <TextField fullWidth label="Ad Object Id" value={adObjectId} onChange={handleAdObjectIdChange} />
          </Grid>
          <Grid item>
            <FormControl>
              <InputLabel>Role</InputLabel>
              <Select value={role} label="Role" onChange={handleRoleChange}>
                {Object.values(UserRole).map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl fullWidth>
              <InputLabel>Topic access</InputLabel>
              <Select
                label="Topic access"
                multiple
                value={selectedTopics}
                onChange={handleTopicChange}
                renderValue={(selected) =>
                  selected.map((id) => topics.find((topic) => topic.id === id)?.name).join(", ")
                }
              >
                {topics.map((topic) => (
                  <MenuItem key={topic.id} value={topic.id}>
                    <Checkbox checked={selectedTopics.indexOf(topic.id) > -1} />
                    <ListItemText primary={topic.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              sx={{ marginRight: 1 }}
              color="primary"
              disabled={formValid() || !isFormChanged()}
              onClick={() => submit(Action.CREATE)}
            >
              Save
            </Button>
            <Button
              variant="contained"
              sx={{ marginRight: 1 }}
              color="secondary"
              disabled={user.id < 0 || !isFormChanged()}
              onClick={handleUndo}
            >
              Undo
            </Button>
            <Button variant="contained" color="warning" onClick={() => submit(Action.DELETE)}>
              Delete
            </Button>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default UserForm;
